import { useState, useEffect, useRef } from "react";
import { useHistory, Link } from "react-router-dom";
import { GlobalStyles } from "@mui/material";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import VuiInput from "components/ui/VuiInput";
import VuiButton from "components/ui/VuiButton";
import VuiAlert from "components/ui/VuiAlert";
import GradientBorder from "components/GradientBorder";
import PageLayout from "components/layout/PageLayout";
import { GRADIENTS } from "utils/designTokens";

import porteriqLogo from "assets/images/logos/porteriq-logo-insights.png";

import { useAuth } from "../../../context/AuthContext";
import { jwtService } from "services";

const NODE_COUNT = 70;
const CONNECTION_DIST = 130;

function SignIn() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [cardLoaded, setCardLoaded] = useState(false);

  const canvasRef = useRef(null);

  const { login, isAuthenticated, loading, user } = useAuth();
  const history = useHistory();

  // Prevent body scroll while on auth page
  useEffect(() => {
    document.documentElement.classList.add("auth-page-hidden-scrollbar");
    document.body.classList.add("auth-page-hidden-scrollbar");
    return () => {
      document.documentElement.classList.remove("auth-page-hidden-scrollbar");
      document.body.classList.remove("auth-page-hidden-scrollbar");
    };
  }, []);

  // Redirect if already authenticated
  useEffect(() => {
    const justLoggedOut = sessionStorage.getItem("justLoggedOut");
    if (justLoggedOut) {
      sessionStorage.removeItem("justLoggedOut");
      return;
    }
    if (isAuthenticated && user && !loading) {
      if (jwtService.isAuthenticated()) {
        if (user.roles && user.roles.includes("CLIENT")) {
          history.replace("/press-digest");
        }
      } else {
        sessionStorage.setItem("justLoggedOut", "true");
      }
    }
  }, [isAuthenticated, loading, user, history]);

  // Card entrance animation
  useEffect(() => {
    const timer = setTimeout(() => setCardLoaded(true), 120);
    return () => clearTimeout(timer);
  }, []);

  // ── Canvas neural-network animation ──────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");

    const setSize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    setSize();

    // Initialise nodes
    const nodes = Array.from({ length: NODE_COUNT }, () => {
      let vx = (Math.random() - 0.5) * 0.7;
      let vy = (Math.random() - 0.5) * 0.7;
      if (Math.abs(vx) < 0.18) vx = vx < 0 ? -0.18 : 0.18;
      if (Math.abs(vy) < 0.18) vy = vy < 0 ? -0.18 : 0.18;
      const isHub = Math.random() < 0.15;
      const isPurple = !isHub && Math.random() < 0.20;
      return {
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        vx,
        vy,
        r: isHub ? 3.5 + Math.random() * 1.5 : 1.3 + Math.random() * 1.5,
        isHub,
        isPurple,
      };
    });

    let animationId;
    let isRunning = true;
    const startTime = Date.now();

    const animate = () => {
      if (!isRunning) return;

      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Global fade-in over ~1.8 s
      const fadeAlpha = Math.min(1, (Date.now() - startTime) / 1800);

      // ── Connections — two-pass glow ──
      for (let i = 0; i < nodes.length; i++) {
        for (let j = i + 1; j < nodes.length; j++) {
          const dx = nodes[i].x - nodes[j].x;
          const dy = nodes[i].y - nodes[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < CONNECTION_DIST) {
            const proximity = 1 - dist / CONNECTION_DIST;
            // Pass 1 — soft glow
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 117, 255, ${fadeAlpha * 0.22 * proximity})`;
            ctx.lineWidth = 3.5;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
            // Pass 2 — bright core
            ctx.beginPath();
            ctx.strokeStyle = `rgba(0, 117, 255, ${fadeAlpha * 0.55 * proximity})`;
            ctx.lineWidth = 0.9;
            ctx.moveTo(nodes[i].x, nodes[i].y);
            ctx.lineTo(nodes[j].x, nodes[j].y);
            ctx.stroke();
          }
        }
      }

      // ── Nodes ──
      nodes.forEach((node) => {
        // Hub glow halo
        if (node.isHub) {
          ctx.save();
          ctx.shadowBlur = 18;
          ctx.shadowColor = "rgba(0, 117, 255, 0.7)";
          ctx.beginPath();
          ctx.arc(node.x, node.y, node.r + 2, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(0, 117, 255, ${fadeAlpha * 0.18})`;
          ctx.fill();
          ctx.restore();
        }

        const alpha = fadeAlpha * (node.isHub ? 1 : node.isPurple ? 0.75 : 0.72);
        ctx.beginPath();
        ctx.fillStyle = node.isHub
          ? `rgba(0, 117, 255, ${alpha})`
          : node.isPurple
          ? `rgba(167, 139, 250, ${alpha})`
          : `rgba(255, 255, 255, ${alpha})`;
        ctx.arc(node.x, node.y, node.r, 0, Math.PI * 2);
        ctx.fill();

        // Move
        node.x += node.vx;
        node.y += node.vy;

        // Bounce off edges
        if (node.x <= node.r || node.x >= canvas.width - node.r) {
          node.vx *= -1;
          node.x = Math.max(node.r, Math.min(node.x, canvas.width - node.r));
        }
        if (node.y <= node.r || node.y >= canvas.height - node.r) {
          node.vy *= -1;
          node.y = Math.max(node.r, Math.min(node.y, canvas.height - node.r));
        }
      });

      animationId = requestAnimationFrame(animate);
    };

    animate();

    const handleResize = () => setSize();
    window.addEventListener("resize", handleResize);

    return () => {
      isRunning = false;
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }
    setIsLoading(true);
    setError("");
    try {
      const result = await login(email, password);
      if (!result.success) {
        setError("Login failed. Please check your email and password and try again.");
        if (result.error) {
          console.error("Login failed due to backend error:", result.error);
        } else {
          console.error("Login failed for an unknown reason.");
        }
      }
    } catch (err) {
      setError("An unexpected error occurred. Please try again later.");
      console.error("Login handleSubmit caught an unexpected error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageLayout background={GRADIENTS.pageBg}>
      {/* CSS keyframes for the morphing blobs */}
      <GlobalStyles
        styles={{
          "@keyframes blobDrift1": {
            "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
            "33%": { transform: "translate(50px, -40px) scale(1.1)" },
            "66%": { transform: "translate(-28px, 24px) scale(0.94)" },
          },
          "@keyframes blobDrift2": {
            "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
            "50%": { transform: "translate(-58px, 30px) scale(1.08)" },
          },
          "@keyframes blobDrift3": {
            "0%, 100%": { transform: "translate(0px, 0px) scale(1)" },
            "40%": { transform: "translate(34px, -46px) scale(1.06)" },
            "80%": { transform: "translate(-20px, 20px) scale(0.96)" },
          },
        }}
      />

      {/* ── Layer 1: CSS morphing color blobs ── */}

      {/* Blue — top left */}
      <VuiBox
        sx={{
          position: "absolute",
          top: "-240px",
          left: "-200px",
          width: "660px",
          height: "660px",
          background:
            "radial-gradient(circle, rgba(0,117,255,0.34) 0%, transparent 68%)",
          filter: "blur(108px)",
          animation: "blobDrift1 22s ease-in-out infinite",
          borderRadius: "62% 38% 72% 28% / 52% 64% 36% 48%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      {/* Purple — bottom right */}
      <VuiBox
        sx={{
          position: "absolute",
          bottom: "-180px",
          right: "-140px",
          width: "600px",
          height: "600px",
          background:
            "radial-gradient(circle, rgba(124,58,237,0.30) 0%, transparent 68%)",
          filter: "blur(118px)",
          animation: "blobDrift2 19s ease-in-out infinite",
          borderRadius: "42% 58% 48% 52% / 62% 38% 62% 38%",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />
      {/* Indigo — mid right */}
      <VuiBox
        sx={{
          position: "absolute",
          top: "30%",
          right: "12%",
          width: "440px",
          height: "440px",
          background:
            "radial-gradient(circle, rgba(37,99,235,0.20) 0%, transparent 68%)",
          filter: "blur(98px)",
          animation: "blobDrift3 26s ease-in-out infinite",
          zIndex: 0,
          pointerEvents: "none",
        }}
      />

      {/* ── Layer 2: Canvas neural-network nodes ── */}
      <canvas
        ref={canvasRef}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 1,
          pointerEvents: "none",
        }}
      />

      {/* ── Layer 3: Centered card ── */}
      <VuiBox
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        px={2}
        position="relative"
        zIndex={2}
        sx={{ overflowY: { xs: "auto", md: "hidden" }, py: { xs: 4, md: 0 } }}
      >
        {/* Gradient border wrapper */}
        <VuiBox
          sx={{
            width: "100%",
            maxWidth: "440px",
            padding: "1.5px",
            borderRadius: "25.5px",
            background:
              "linear-gradient(135deg, rgba(0,117,255,0.65) 0%, rgba(124,58,237,0.45) 100%)",
            boxShadow:
              "0 28px 90px rgba(0,0,0,0.75), 0 0 130px rgba(0,117,255,0.12)",
            opacity: cardLoaded ? 1 : 0,
            transform: cardLoaded ? "translateY(0)" : "translateY(26px)",
            transition: "opacity 0.75s ease-out, transform 0.75s ease-out",
          }}
        >
        <VuiBox
          sx={{
            background: "rgba(6, 10, 20, 0.55)",
            backdropFilter: "blur(22px)",
            borderRadius: "24px",
            padding: { xs: "32px 24px", sm: "44px 40px" },
          }}
        >
          {/* Logo + tagline */}
          <VuiBox
            display="flex"
            flexDirection="column"
            alignItems="center"
            mb={3.5}
          >
            <a
              href="https://www.porteriq.com/"
              style={{ display: "block", marginBottom: "10px" }}
            >
              <VuiBox
                component="img"
                src={porteriqLogo}
                alt="PorterIQ"
                sx={{ height: "96px", objectFit: "contain" }}
              />
            </a>
            <VuiTypography
              sx={{
                color: "#78A7FC",
                fontSize: "0.6rem",
                letterSpacing: "0.22em",
                textTransform: "uppercase",
                fontWeight: "700",
                opacity: 0.9,
              }}
            >
              Competitive Intelligence Hub
            </VuiTypography>
          </VuiBox>

          {/* Heading */}
          <VuiBox textAlign="center" mb={3}>
            <VuiTypography
              variant="h3"
              color="white"
              fontWeight="bold"
              mb={0.75}
            >
              Welcome back
            </VuiTypography>
            <VuiTypography
              variant="body2"
              sx={{ color: "rgba(255,255,255,0.5)", fontSize: "0.875rem" }}
            >
              Enter your business credentials to access your account
            </VuiTypography>
          </VuiBox>

          {/* Form */}
          <VuiBox component="form" role="form" onSubmit={handleSubmit}>
            {/* Error alert */}
            {error && (
              <VuiAlert
                color="error"
                dismissible
                mb={2}
                sx={(theme) => ({
                  backgroundImage: "none",
                  backgroundColor: "rgba(90,160,230,0.25)",
                  border: "1px solid rgba(90,160,230,0.45)",
                  borderRadius: theme.borders.borderRadius.md,
                  padding: theme.spacing(1.25, 2),
                  minHeight: "auto",
                  boxShadow: "none",
                  "& > .MuiBox-root": {
                    color: `${theme.palette.common.white} !important`,
                    fontSize: theme.typography.pxToRem(13.5),
                    fontWeight: theme.typography.fontWeightRegular,
                    lineHeight: 1.45,
                    padding: theme.spacing(0.5, 0),
                  },
                  '& > span[class*="VuiAlertCloseIcon"]': {
                    color: `${theme.palette.grey[400]} !important`,
                    fontSize: theme.typography.pxToRem(18),
                    opacity: 0.8,
                    right: theme.spacing(1.25),
                    top: "50%",
                    transform: "translateY(-50%)",
                    padding: theme.spacing(0.5),
                    "&:hover": {
                      color: `${theme.palette.common.white} !important`,
                      opacity: 1,
                      backgroundColor: "rgba(90,160,230,0.15)",
                    },
                  },
                })}
              >
                {error}
              </VuiAlert>
            )}

            {/* Business Email */}
            <VuiBox mb={2.5}>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography
                  component="label"
                  variant="button"
                  color="white"
                  fontWeight="medium"
                >
                  Business Email
                </VuiTypography>
              </VuiBox>
              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius="10px"
                backgroundImage={GRADIENTS.cardBorder}
              >
                <VuiInput
                  type="email"
                  placeholder="you@company.com"
                  value={email}
                  autoComplete="email"
                  onChange={(e) => setEmail(e.target.value)}
                  sx={({ typography: { size } }) => ({
                    fontSize: size.sm,
                    padding: "12px 16px",
                    borderRadius: "9px",
                    width: "100%",
                  })}
                />
              </GradientBorder>
              <VuiTypography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  color: "#78A7FC",
                  display: "block",
                  mt: 0.5,
                  ml: 0.5,
                }}
              >
                Sign in is only available with a business email address
              </VuiTypography>
            </VuiBox>

            {/* Password */}
            <VuiBox mb={3.5}>
              <VuiBox mb={0.5} ml={0.5}>
                <VuiTypography
                  component="label"
                  variant="button"
                  color="white"
                  fontWeight="medium"
                >
                  Password
                </VuiTypography>
              </VuiBox>
              <GradientBorder
                minWidth="100%"
                padding="1px"
                borderRadius="10px"
                backgroundImage={GRADIENTS.cardBorder}
              >
                <VuiInput
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  autoComplete="current-password"
                  onChange={(e) => setPassword(e.target.value)}
                  sx={({ typography: { size } }) => ({
                    fontSize: size.sm,
                    padding: "12px 16px",
                    borderRadius: "9px",
                    width: "100%",
                  })}
                  endAdornment={
                    <VuiBox
                      color="white"
                      onClick={togglePasswordVisibility}
                      sx={{
                        cursor: "pointer",
                        mr: 1,
                        opacity: 0.65,
                        "&:hover": { opacity: 1 },
                      }}
                    >
                      {showPassword ? (
                        <i className="fa fa-eye-slash" />
                      ) : (
                        <i className="fa fa-eye" />
                      )}
                    </VuiBox>
                  }
                />
              </GradientBorder>
            </VuiBox>

            {/* Sign In button */}
            <VuiBox mb={2.5}>
              <VuiButton
                type="submit"
                color="info"
                fullWidth
                disabled={isLoading}
                sx={{
                  backgroundColor: "#1A73E8",
                  color: "white",
                  padding: "12px 0",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  letterSpacing: "0.02em",
                  boxShadow: "0 4px 20px rgba(26,115,232,0.35)",
                  "&:hover": {
                    backgroundColor: "#1767D2",
                    boxShadow: "0 4px 28px rgba(26,115,232,0.5)",
                  },
                  "&:disabled": { opacity: 0.7 },
                }}
              >
                {isLoading ? "Signing in..." : "Sign in"}
              </VuiButton>
            </VuiBox>

            {/* Forgot password link */}
            <VuiBox mb={2} textAlign="center">
              <VuiTypography
                component={Link}
                to="/authentication/forgot-password"
                variant="caption"
                sx={{
                  color: "rgba(255,255,255,0.45)",
                  fontSize: "0.75rem",
                  textDecoration: "none",
                  "&:hover": { color: "rgba(255,255,255,0.75)" },
                }}
              >
                Forgot your password?
              </VuiTypography>
            </VuiBox>

            {/* Footer note */}
            <VuiTypography
              variant="caption"
              sx={{
                color: "rgba(255,255,255,0.28)",
                fontSize: "0.68rem",
                display: "block",
                textAlign: "center",
                lineHeight: 1.5,
              }}
            >
              PorterIQ is a business platform. Contact your administrator if
              you need access.
            </VuiTypography>
          </VuiBox>
        </VuiBox>
        </VuiBox>
      </VuiBox>
    </PageLayout>
  );
}

export default SignIn;
